import React from 'react';

import { Icon, IconButton, Pressable } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Box, withTypography } from 'components/ui';
import type { BoxProps } from 'components/ui';
import { primitive, spacing } from 'styles/constants';

const CARD_WIDTH = 152;
const CARD_WIDTH_PX = '152px';
const CARD_HEIGHT_PX = '200px';
const IMAGE_WIDTH = 100;
const IMAGE_HEIGHT = 100;

const styles = StyleSheet.create({
  rewardText: {
    marginBottom: 0,
    paddingLeft: spacing.$1,
    paddingRight: spacing.$1,
    textAlign: 'center',
  },
  cardContainer: {
    marginRight: spacing.$2,
    overflow: 'hidden',
  },
  imageWrapper: {
    height: IMAGE_HEIGHT,
    justifyContent: 'center',
    width: IMAGE_WIDTH,
  },
  infoContainer: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  inlineAlertContainer: {
    alignItems: 'center',
    backgroundColor: primitive.$blackOpacity4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    bottom: 0,
    flexDirection: 'row',
    gap: spacing.$1,
    justifyContent: 'center',
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$2,
    position: 'absolute',
    width: CARD_WIDTH,
  },
  inlineAlertSpacer: {
    height: spacing.$6,
  },
});

export const RewardText = withTypography({
  variant: 'headerThreeLook',
  ellipsizeMode: 'tail',
  numberOfLines: 2,
  fontSize: 'xs',
  lineHeight: 'xs',
  style: styles.rewardText,
});

export const BackgroundImage = Picture.withConfig({
  width: '100%',
  height: '100%',
  position: 'absolute',
});

export const CardContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.cardContainer, style] });

export const StyledClickableContainer = Pressable.withConfig<{
  isLocked: boolean;
  isApplied: boolean;
}>(p => ({
  width: CARD_WIDTH_PX,
  height: CARD_HEIGHT_PX,
  paddingTop: '$1',
  paddingX: '$3',
  paddingBottom: '$4',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 'xl',
  justifyContent: 'space-between',
  background: p.isLocked ? primitive.$blackOpacity10 : primitive.$white,
  ...(p.isApplied
    ? {
        borderWidth: '2',
        borderColor: primitive.$bbqBrown,
        borderStyle: 'solid',
      }
    : {}),
}));

export const ImageWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.imageWrapper, style] });

export const InfoContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.infoContainer, style] });

export const InlineAlertContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.inlineAlertContainer, style] });

export const InlineAlertSpacer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.inlineAlertSpacer, style] });

export const InlineAlertText = withTypography({ fontSize: 'xs' });

export const StyledRewardLock = Icon.withConfig({
  variant: 'locked',
  size: '$6',
  position: 'absolute',
  bottom: '$4',
  margin: '$2',
});

export const AlertIcon = Icon.withConfig({
  name: 'mi:info',
  size: '$4',
});

export const AddIconButton = IconButton.withConfig({
  padding: '$0',
  background: primitive.$meltyYellow,
  borderColor: primitive.$meltyYellow,
  width: '44px',
  height: '44px',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  bottom: '15px',
  _icon: {
    color: primitive.$bbqBrown,
    size: '$5',
  },
});

export const IncentiveCardDescription = SanityBlockRenderer.withConfig({
  width: 'full',
  overflow: 'hidden',
  textProps: {
    numberOfLines: 2,
    fontSize: 12,
    lineHeight: 16,
    ellipsizeMode: 'tail',
  },
});

export const PointsNeededText = withTypography({ fontSize: 'sm' });

export const PointsNeededTextDetail = withTypography({ color: primitive.$fieryRed });
